/**
 * ColumnFilter — Excel-style per-column header control for the bulk editor.
 *
 * Provides:
 *   • Sort A→Z / Z→A (alphabetical, locale-aware, numeric-friendly)
 *   • A checkbox list of the column's distinct values (with counts) + search,
 *     so the admin can include only certain values (like Excel's funnel).
 *
 * The popover is rendered in a body portal with fixed positioning so it escapes
 * the table's horizontal-scroll clipping.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Filter, ArrowDownAZ, ArrowUpAZ, Search, Check } from 'lucide-react';

export default function ColumnFilter({
  col, rows, sortConfig, setSortConfig, columnFilters, setColumnFilters, normalizeList,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [q, setQ] = useState('');
  const [temp, setTemp] = useState(null); // null = "all selected"
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const active = !!(columnFilters[col.key] && columnFilters[col.key].size);
  const sortedAsc = sortConfig.key === col.key && sortConfig.dir === 'asc';
  const sortedDesc = sortConfig.key === col.key && sortConfig.dir === 'desc';

  function valuesFor(row) {
    const raw = row[col.key];
    if (col.kind === 'chips') { const a = normalizeList(raw); return a.length ? a : ['(Vacías)']; }
    if (col.kind === 'bool') return [raw ? 'Sí' : 'No'];
    const s = (raw ?? '').toString().trim();
    return [s || '(Vacías)'];
  }

  const distinct = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => valuesFor(r).forEach((v) => m.set(v, (m.get(v) || 0) + 1)));
    return Array.from(m.entries()).sort((a, b) => {
      if (a[0] === '(Vacías)') return 1;
      if (b[0] === '(Vacías)') return -1;
      return a[0].localeCompare(b[0], 'es', { numeric: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const allVals = useMemo(() => distinct.map((d) => d[0]), [distinct]);
  const shown = useMemo(
    () => distinct.filter(([v]) => v.toLowerCase().includes(q.toLowerCase())),
    [distinct, q],
  );

  useEffect(() => {
    if (open) { setTemp(columnFilters[col.key] ? new Set(columnFilters[col.key]) : null); setQ(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    function place() {
      const rect = triggerRef.current.getBoundingClientRect();
      const w = 240;
      let left = rect.right - w;
      if (left < 8) left = 8;
      setPos({ top: rect.bottom + 4, left, width: w });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const isChecked = (v) => (temp === null ? true : temp.has(v));

  function toggle(v) {
    setTemp((prev) => {
      const base = prev === null ? new Set(allVals) : new Set(prev);
      if (base.has(v)) base.delete(v); else base.add(v);
      return base;
    });
  }
  function setShownOnly(check) {
    setTemp((prev) => {
      const base = prev === null ? new Set(allVals) : new Set(prev);
      shown.forEach(([v]) => { if (check) base.add(v); else base.delete(v); });
      return base;
    });
  }
  function apply() {
    setColumnFilters((cf) => {
      const next = { ...cf };
      if (temp === null || temp.size >= allVals.length) delete next[col.key];
      else next[col.key] = temp;
      return next;
    });
    setOpen(false);
  }
  function clearAll() {
    setColumnFilters((cf) => { const n = { ...cf }; delete n[col.key]; return n; });
    setOpen(false);
  }
  function applySort(dir) { setSortConfig({ key: col.key, dir }); setOpen(false); }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`col-filter-${col.key}`}
        title="Filtrar / ordenar"
        className={`p-0.5 rounded transition-colors flex-shrink-0 ${
          active || sortedAsc || sortedDesc
            ? 'text-cmic-600 bg-cmic-100'
            : 'text-charcoal-300 hover:text-charcoal-600 hover:bg-gray-100'
        }`}
      >
        {sortedAsc ? <ArrowDownAZ size={13} /> : sortedDesc ? <ArrowUpAZ size={13} /> : <Filter size={12} />}
      </button>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 60 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl p-2 normal-case"
          data-testid={`col-filter-panel-${col.key}`}
        >
          <div className="flex gap-1 mb-2">
            <button type="button" onClick={() => applySort('asc')} className={`flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded-lg border ${sortedAsc ? 'bg-cmic-50 border-cmic-300 text-cmic-700' : 'border-gray-200 text-charcoal-600 hover:bg-gray-50'}`}>
              <ArrowDownAZ size={12} /> A→Z
            </button>
            <button type="button" onClick={() => applySort('desc')} className={`flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded-lg border ${sortedDesc ? 'bg-cmic-50 border-cmic-300 text-cmic-700' : 'border-gray-200 text-charcoal-600 hover:bg-gray-50'}`}>
              <ArrowUpAZ size={12} /> Z→A
            </button>
          </div>
          <div className="relative mb-1.5">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-charcoal-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar valor…"
              data-testid={`col-filter-search-${col.key}`}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cmic-300"
            />
          </div>
          <div className="flex justify-between px-1 mb-1">
            <button type="button" onClick={() => setShownOnly(true)} className="text-[10px] text-cmic-600 hover:underline">Seleccionar todo</button>
            <button type="button" onClick={() => setShownOnly(false)} className="text-[10px] text-charcoal-500 hover:underline">Ninguno</button>
          </div>
          <div className="max-h-48 overflow-y-auto -mx-0.5">
            {shown.length === 0 && <div className="text-[11px] text-charcoal-400 italic px-2 py-2 text-center">Sin valores.</div>}
            {shown.map(([v, count]) => (
              <label key={v} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-gray-50 rounded cursor-pointer">
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isChecked(v) ? 'bg-cmic-600 border-cmic-600 text-white' : 'border-gray-300'}`}>
                  {isChecked(v) && <Check size={11} />}
                </span>
                <input type="checkbox" className="sr-only" checked={isChecked(v)} onChange={() => toggle(v)} />
                <span className="truncate flex-1 text-charcoal-700">{v}</span>
                <span className="text-[10px] text-charcoal-400">{count}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={clearAll} className="text-[11px] text-charcoal-500 hover:text-rose-600">Limpiar</button>
            <button type="button" onClick={apply} data-testid={`col-filter-apply-${col.key}`} className="bg-cmic-600 hover:bg-cmic-700 text-white text-[11px] font-medium px-3 py-1.5 rounded-lg">Aplicar</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
